import React, { CSSProperties, MutableRefObject, useCallback } from 'react'
import { Pair, CurrencyAmount } from 'vvs-sdk'
import { Text } from 'vvs-uikit'
import styled from 'styled-components'
import { FixedSizeList } from 'react-window'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useTokenBalance } from 'state/wallet/hooks'
import Column from '../Layout/Column'
import { RowFixed, RowBetween } from '../Layout/Row'
import { DoubleCurrencyLogo } from '../Logo'
import CircleLoader from '../Loader/CircleLoader'

const StyledBalanceText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  max-width: 5rem;
  text-overflow: ellipsis;
`

function Balance({ balance }: { balance: CurrencyAmount }) {
  return <StyledBalanceText title={balance.toExact()}>{balance.toSignificant(4)}</StyledBalanceText>
}

const MenuItem = styled(RowBetween)<{ disabled: boolean; selected?: boolean }>`
  padding: 4px 20px;
  height: 56px;
  display: grid;
  grid-template-columns: auto minmax(auto, 1fr) minmax(0, 72px);
  grid-gap: 8px;
  cursor: ${({ disabled }) => !disabled && 'pointer'};
  pointer-events: ${({ disabled }) => disabled && 'none'};
  :hover {
    background-color: ${({ theme, disabled }) => !disabled && theme.colors.background};
  }
  opacity: ${({ disabled, selected }) => (disabled || selected ? 0.5 : 1)};
`

function PairRow({
  pair,
  onSelect,
  isSelected,
  style,
}: {
  pair: Pair
  onSelect: () => void
  isSelected: boolean
  style: CSSProperties
}) {
  const { account } = useActiveWeb3React()
  const key = pair.liquidityToken.address
  const balance = useTokenBalance(account ?? undefined, pair.liquidityToken)

  // only show add or remove buttons if not on selected list
  return (
    <MenuItem
      style={style}
      className={`token-item-${key}`}
      onClick={() => (isSelected ? null : onSelect())}
      disabled={isSelected}
    >
      <DoubleCurrencyLogo currency0={pair.token0} currency1={pair.token1} size={16} margin />
      <Column>
        <Text bold>
          {pair?.token0.symbol}-{pair?.token1.symbol}
        </Text>
      </Column>
      <RowFixed style={{ justifySelf: 'flex-end' }}>
        {balance ? <Balance balance={balance} /> : account ? <CircleLoader /> : null}
      </RowFixed>
    </MenuItem>
  )
}

export default function PairList({
  height,
  pairs,
  selectedPair,
  onPairSelect,
  fixedListRef,
}: {
  height: number
  pairs: Pair[]
  selectedPair?: Pair | null
  onPairSelect: (pair: Pair) => void
  fixedListRef?: MutableRefObject<FixedSizeList | undefined>
}) {
  const itemData: (Pair | undefined)[] = pairs

  const Row = useCallback(
    ({ data, index, style }) => {
      const pair: Pair = data[index]
      const isSelected = Boolean(selectedPair && selectedPair.liquidityToken.address === pair.liquidityToken.address)
      const handleSelect = () => onPairSelect(pair)

      return <PairRow style={style} pair={pair} isSelected={isSelected} onSelect={handleSelect} />
    },
    [selectedPair, onPairSelect],
  )

  const itemKey = useCallback((index: number, data: any) => data[index].liquidityToken.address, [])

  return (
    <FixedSizeList
      height={height}
      ref={fixedListRef as any}
      width="100%"
      itemData={itemData}
      itemCount={itemData.length}
      itemSize={56}
      itemKey={itemKey}
    >
      {Row}
    </FixedSizeList>
  )
}
